/**
 * "Sign in with Microsoft" button, drawn to the redlines Microsoft publishes at
 * https://learn.microsoft.com/entra/identity-platform/howto-add-branding-in-apps:
 * 41px tall, 215px wide (104px for the shorter "Sign in" label), the unaltered
 * 21x21 Microsoft logo 12px in from the left edge, 12px of space before 15px
 * Segoe UI label text. Light scheme is #FFFFFF inside a 1px #8C8C8C border with
 * #5E5E5E text; dark scheme is #2F2F2F with no border and #FFFFFF text.
 * The two label strings are the only ones Microsoft permits -- do not reword them,
 * and do not alter the logo.
 */

interface MicrosoftSignInButtonProps {
  onClick: () => void
  disabled?: boolean
  /** Microsoft allows the shorter "Sign in" label only where the full one doesn't fit. */
  short?: boolean
  className?: string
}

/** Segoe UI first, with the stack Microsoft's own web surfaces fall back through. */
const SEGOE_UI =
  '"Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif'

export default function MicrosoftSignInButton({
  onClick,
  disabled = false,
  short = false,
  className = '',
}: MicrosoftSignInButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{ width: short ? 104 : 215, height: 41 }}
      className={`inline-flex items-center px-3 bg-white border border-[#8C8C8C] hover:bg-[#F3F3F3] dark:bg-[#2F2F2F] dark:border-[#2F2F2F] dark:hover:bg-[#3B3B3B] disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${className}`}
    >
      <svg width="21" height="21" viewBox="0 0 21 21" aria-hidden="true" className="shrink-0">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
      </svg>
      <span
        style={{ fontFamily: SEGOE_UI, fontSize: 15, marginLeft: 12 }}
        className="leading-none whitespace-nowrap text-[#5E5E5E] dark:text-white"
      >
        {short ? 'Sign in' : 'Sign in with Microsoft'}
      </span>
    </button>
  )
}
